import React from "react";
import { MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import type { Product } from "@/hooks/useProducts";
import type { Category } from "@/hooks/useCategories";
import type { ArtistDetail } from "@/hooks/useArtistDetails";

interface ProductTableProps {
  products: Product[];
  categories: Category[];
  artistDetails: ArtistDetail[];
  loading: boolean;
  onEdit: (product: Product) => void;
  onDelete: (productId: string) => Promise<boolean>;
}

export function ProductTable({
  products,
  categories,
  artistDetails,
  loading,
  onEdit,
  onDelete,
}: ProductTableProps) {
  const { toast } = useToast();
  
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(price);
  };

  const handleDelete = async (productId: string) => {
    if (!confirm("Are you sure you want to delete this product?")) return;
    
    try {
      const success = await onDelete(productId);
      if (success) {
        toast({
          title: "Success",
          description: "Product deleted successfully",
        });
      } else {
        throw new Error("Failed to delete product");
      }
    } catch (error) {
      console.error("Error deleting product:", error);
      toast({
        title: "Error",
        description: "Failed to delete product. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Products</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">Loading products...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Products ({products.length})</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Artist</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Variants</TableHead>
              <TableHead>Rating</TableHead>
              <TableHead>Stock</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.map((product) => (
              <TableRow key={product.id}>
                <TableCell>
                  <div className="flex items-center space-x-3">
                    {product.main_image && (
                      <img
                        src={product.main_image}
                        alt={product.name}
                        className="w-12 h-12 object-cover rounded"
                      />
                    )}
                    <div>
                      <div className="font-medium">{product.name}</div>
                      <div className="text-sm text-gray-500">
                        {product.description?.substring(0, 50)}
                        {product.description && product.description.length > 50 && "..."}
                      </div>
                    </div>
                  </div>
                </TableCell>
                
                <TableCell>
                  <Badge variant="secondary">
                    {(() => {
                      const categoryObj = categories.find(cat => cat.slug === product.category);
                      return categoryObj?.name || product.category;
                    })()}
                  </Badge>
                </TableCell>
                
                <TableCell>
                  <div className="font-medium">
                    {product.artistid ? (
                      artistDetails.find(a => a.artistid === product.artistid)?.full_name || product.artistid
                    ) : (
                      "No Artist"
                    )}
                  </div>
                </TableCell>
                
                <TableCell>
                  <div className="font-medium">{formatPrice(product.price)}</div>
                  {product.original_price && (
                    <div className="text-sm text-gray-500 line-through">
                      {formatPrice(product.original_price)}
                    </div>
                  )}
                </TableCell>
                
                <TableCell>
                  <div className="space-y-1">
                    {product.sizes && product.sizes.length > 0 ? (
                      product.sizes
                        .filter(size => size.is_available)
                        .slice(0, 3)
                        .map((size) => (
                          <div key={size.id} className="text-xs">
                            <span className="font-medium">{size.size_display_name}</span>
                            <span className="text-gray-500 ml-1">₹{size.price}</span>
                          </div>
                        ))
                    ) : (
                      <span className="text-gray-500 text-sm">No variants</span>
                    )}
                    {product.sizes && product.sizes.length > 3 && (
                      <div className="text-xs text-gray-500">
                        +{product.sizes.length - 3} more
                      </div>
                    )}
                  </div>
                </TableCell>
                
                <TableCell>
                  <div className="flex items-center">
                    <span className="text-yellow-500">★</span>
                    <span className="ml-1">{product.rating}</span>
                    <span className="text-gray-500 ml-1">({product.reviews_count})</span>
                  </div>
                </TableCell>
                
                <TableCell>
                  <Badge variant={product.in_stock ? "default" : "destructive"}>
                    {product.in_stock ? "In Stock" : "Out of Stock"}
                  </Badge>
                </TableCell>
                
                <TableCell>
                  <div className="text-sm text-gray-500">
                    {new Date(product.created_at).toLocaleDateString()}
                  </div>
                </TableCell>
                
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-8 w-8 p-0">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>Actions</DropdownMenuLabel>
                      <DropdownMenuItem onClick={() => onEdit(product)}>
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => handleDelete(product.id)}
                        className="text-red-600"
                      >
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
